import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import axios from "axios";
import {
    IoArrowBack,
    IoAddCircle,
    IoClose,
    IoImageOutline,
    IoImagesOutline,
    IoLogoWhatsapp,
    IoSearch,
    IoCloseCircle,
    IoCreateOutline,
    IoTrashOutline,
} from "react-icons/io5";

import {
    getCustomUser,
    getChatGroupById,
    updateChatGroup,
    getElans,
    createElan,
    updateElan,
    deleteElan,
    getUsers,
    updateUser,
} from "../api";

import { getStableDeviceId } from "../utils/deviceId";
import "../styles/sifaris.css";

const ONE_SIGNAL_APP_ID = "26a128f2-4ccf-4dde-9986-2b27bb1a25b2";
const ONE_SIGNAL_REST_API_KEY =
    "os_v2_app_e2qsr4smz5g55gmgfmt3wgrfwlr3fpv76kau37n2xxvu2m2krc6g3wi4dhudjinuajtqdxnsbt5rlnnumq6osxxaalhxyhmzkypia5q";

const apiKeys = [
    "4b8d7989751c89d9561fd1e44100bf68",
    "2c5257b8de4e5bf05fc5a7b2419a418e",
    "62346dd09fb59412e2774c150742d3a4",
];

const isValidUUID = (s) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        String(s || "").trim()
    );

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

const truncateWithEllipsis = (str = "", max = 100) => {
    if (!str) return "";
    return str.length > max ? `${str.slice(0, max).trim()}…` : str;
};

const parsePrice = (v) => {
    const cleaned = String(v ?? "")
        .replace(/\s+/g, "")
        .replace(",", ".")
        .replace(/[^\d.]/g, "");
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : NaN;
};

const formatAZN = (v) => {
    const n = Number(v);
    if (Number.isNaN(n)) return v;
    return `${n.toLocaleString("az-AZ")} AZN`;
};

const formatPhoneForWA = (phoneRaw) => {
    const only = String(phoneRaw || "").replace(/\D/g, "");
    if (only.startsWith("0")) return `994${only.slice(1)}`;
    if (only.startsWith("994")) return only;
    return only;
};

const ShopGroupScreen = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const routeState = location.state || {};

    const groupId = routeState.groupId ?? 3;
    const group = routeState.group ?? { name: "Alqı-satqı qrupu" };

    const [items, setItems] = useState([]);
    const [listRows, setListRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [myUserId, setMyUserId] = useState(null);
    const [editorOpen, setEditorOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [title, setTitle] = useState("");
    const [desc, setDesc] = useState("");
    const [price, setPrice] = useState("");
    const [images, setImages] = useState([]);
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerImages, setViewerImages] = useState([]);
    const [viewerIndex, setViewerIndex] = useState(0);
    const [phoneCache, setPhoneCache] = useState({});
    const [expandedDescIds, setExpandedDescIds] = useState(new Set());
    const [isMuted, setIsMuted] = useState(false);
    const [search, setSearch] = useState("");
    const [modal, setModal] = useState({
        open: false,
        title: "",
        message: "",
        type: "info",
    });

    const fileInputRef = useRef(null);

    const showModal = ({ title, message, type = "info" }) => {
        setModal({ open: true, title, message, type });
    };

    const requestWithRetry = async (fn, { tries = 3, baseMs = 600 } = {}) => {
        let lastErr;
        for (let i = 0; i < tries; i += 1) {
            try {
                return await fn();
            } catch (e) {
                lastErr = e;
                const code = e?.response?.status;
                const retriable = code === 429 || (code >= 500 && code < 600);
                if (!retriable || i === tries - 1) throw e;
                const wait = baseMs * Math.pow(2, i);
                await delay(wait);
            }
        }
        throw lastErr;
    };

    const getEffectiveUserId = async (fallback) => {
        if (fallback) return Number(fallback);
        const fromStorage = localStorage.getItem("id");
        return fromStorage ? Number(fromStorage) : null;
    };

    const resetForm = () => {
        setEditing(null);
        setTitle("");
        setDesc("");
        setPrice("");
        setImages([]);
    };

    const openCreator = () => {
        resetForm();
        setEditorOpen(true);
    };

    const openEditor = (it) => {
        setEditing(it);
        setTitle(it.title || "");
        setDesc(it.description || "");
        setPrice(String(it.price ?? ""));
        setImages((it.images || []).map((u, idx) => ({
            id: `remote-${idx}`,
            uri: u,
            remote: true,
            base64: null,
            file: null,
        })));
        setEditorOpen(true);
    };

    const groupByTwo = (list = []) => {
        const rows = [];
        for (let i = 0; i < list.length; i += 2) {
            rows.push({
                type: "row",
                key: `row_${list[i]?.id || i}_${list[i + 1]?.id || "x"}`,
                left: list[i],
                right: list[i + 1] || null,
            });
        }
        return rows;
    };

    const fetchPhonesForItems = useCallback(async (arr = []) => {
        try {
            const ids = Array.from(
                new Set(
                    arr
                        .map((e) => Number(e?.userId))
                        .filter((id) => Number.isFinite(id) && !phoneCache[id])
                )
            );

            if (!ids.length) return;

            const results = await Promise.allSettled(
                ids.map(async (id) => {
                    const res = await getCustomUser(id);
                    const phone = res?.data?.phoneNumber || "";
                    return { id, phone };
                })
            );

            const next = {};
            results.forEach((r) => {
                if (r.status === "fulfilled" && r.value) {
                    next[r.value.id] = r.value.phone;
                }
            });

            if (Object.keys(next).length) {
                setPhoneCache((prev) => ({ ...prev, ...next }));
            }
        } catch (err) {
            console.log("fetchPhonesForItems error", err?.message || err);
        }
    }, [phoneCache]);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const res = await getElans();
            const all = Array.isArray(res?.data) ? res.data : [];

            const effectiveUserId = await getEffectiveUserId(myUserId);

            const visible = all.filter((e) => e && e.isDisable !== true);
            const sorted = visible.sort((a, b) => {
                const ta = new Date(a.createdAt || a.updatedAt || 0).getTime();
                const tb = new Date(b.createdAt || b.updatedAt || 0).getTime();
                return tb - ta;
            });

            await fetchPhonesForItems(sorted);

            const mine = sorted.filter((e) => Number(e.userId) === Number(effectiveUserId));
            const others = sorted.filter((e) => Number(e.userId) !== Number(effectiveUserId));

            const rows = [];
            const mineRows = groupByTwo(mine);
            const otherRows = groupByTwo(others);

            if (mineRows.length) {
                rows.push({ type: "section", key: "sec_mine", title: "Mənim elanlarım" });
                rows.push(...mineRows);
            }

            if (otherRows.length) {
                rows.push({ type: "section", key: "sec_others", title: "Digər elanlar" });
                rows.push(...otherRows);
            }

            setItems(sorted);
            setListRows(rows);
        } catch (e) {
            console.log("getElans error", e);
            showModal({
                title: "Xəta",
                message: "Elanlar yüklənə bilmədi.",
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    }, [myUserId, fetchPhonesForItems]);

    const refreshMuteState = useCallback(async (uid) => {
        try {
            if (!groupId || !uid) return;
            const groupRes = await getChatGroupById(groupId);
            const list = Array.isArray(groupRes?.data?.mutedUserIds)
                ? groupRes.data.mutedUserIds.map(Number)
                : [];
            setIsMuted(list.includes(Number(uid)));
        } catch (e) {
            console.error("refreshMuteState error:", e);
        }
    }, [groupId]);

    useEffect(() => {
        const initializeApp = async () => {
            try {
                const storedUserId = localStorage.getItem("id");
                const storedPassword = localStorage.getItem("password");
                const stableDeviceId = await getStableDeviceId();

                localStorage.setItem("deviceUUID", stableDeviceId);
                if (storedUserId) setMyUserId(Number(storedUserId));

                if (storedUserId) {
                    const response = await getCustomUser(storedUserId);

                    if (response?.data) {
                        if (response.data.isDisable) {
                            const hasExpiry =
                                response.data.disableExpiryDate &&
                                String(response.data.disableExpiryDate).trim().length > 0;

                            if (hasExpiry) {
                                const disableExpiryDate = moment(
                                    response.data.disableExpiryDate,
                                    "YYYY-MM-DD HH:mm:ss",
                                    true
                                );
                                const currentTime = moment();

                                if (disableExpiryDate.isValid() && disableExpiryDate.isBefore(currentTime)) {
                                    await updateUser(storedUserId, {
                                        isDisable: false,
                                        disableExpiryDate: "",
                                    });
                                } else {
                                    localStorage.clear();
                                    navigate("/sifaris/login", { replace: true });
                                    return;
                                }
                            } else {
                                localStorage.clear();
                                navigate("/sifaris/login", { replace: true });
                                return;
                            }
                        }

                        if (storedPassword && response.data.password !== storedPassword) {
                            localStorage.clear();
                            navigate("/sifaris/login", { replace: true });
                            return;
                        }

                        const storedDeviceId = localStorage.getItem("deviceUUID");
                        if (
                            storedDeviceId &&
                            response.data.phoneId &&
                            storedDeviceId !== response.data.phoneId
                        ) {
                            localStorage.clear();
                            navigate("/sifaris/login", { replace: true });
                            return;
                        }
                    }
                }

                await load();
                await refreshMuteState(storedUserId);
            } catch (error) {
                console.error("ShopGroup init error:", error);
            }
        };

        initializeApp();
    }, [load, navigate, refreshMuteState]);

    useEffect(() => {
        if (!Array.isArray(items)) return;

        const q = search.trim().toLowerCase();
        const filtered = q
            ? items.filter((e) => (e?.title || "").toLowerCase().includes(q))
            : items;

        const mine = filtered.filter((e) => Number(e.userId) === Number(myUserId));
        const others = filtered.filter((e) => Number(e.userId) !== Number(myUserId));

        const rows = [];

        if (q) {
            rows.push({
                type: "section",
                key: "sec_search",
                title: `Axtarış: "${search}" • ${filtered.length} nəticə`,
            });
            rows.push(...groupByTwo(filtered));
        } else {
            const mineRows = groupByTwo(mine);
            const otherRows = groupByTwo(others);

            if (mineRows.length) {
                rows.push({ type: "section", key: "sec_mine", title: "Mənim elanlarım" });
                rows.push(...mineRows);
            }

            if (otherRows.length) {
                rows.push({ type: "section", key: "sec_others", title: "Digər elanlar" });
                rows.push(...otherRows);
            }
        }

        setListRows(rows);
    }, [items, myUserId, search]);

    const toggleMute = async () => {
        try {
            const uidStr = localStorage.getItem("id");
            const uid = Number(uidStr);
            if (!groupId || !uid) return;

            const groupRes = await getChatGroupById(groupId);
            const currentList = Array.isArray(groupRes?.data?.mutedUserIds)
                ? groupRes.data.mutedUserIds.map(Number)
                : [];

            const isCurrentlyMuted = currentList.includes(uid);
            const nextList = isCurrentlyMuted
                ? currentList.filter((id) => id !== uid)
                : [...currentList, uid];

            await updateChatGroup(groupId, { mutedUserIds: nextList });
            setIsMuted(!isCurrentlyMuted);
        } catch (e) {
            console.error("handleMute error:", e);
        }
    };

    const collectGroupPushTargets = async (groupIdForMute, senderId) => {
        try {
            const usersRes = await getUsers();
            const groupResp = await getChatGroupById(groupIdForMute);
            const muted = Array.isArray(groupResp?.data?.mutedUserIds)
                ? groupResp.data.mutedUserIds.map(Number)
                : [];

            return (usersRes?.data || [])
                .filter(
                    (u) =>
                        Number(u.id) !== Number(senderId) &&
                        !muted.includes(Number(u.id)) &&
                        u.oneSignal
                )
                .map((u) => u.oneSignal);
        } catch (e) {
            console.log("collectGroupPushTargets error:", e?.message);
            return [];
        }
    };

    const sendPushNotification = async (ids, messageTitle, messageBody) => {
        const subs = (Array.isArray(ids) ? ids : [ids])
            .map((x) => String(x || "").trim())
            .filter(isValidUUID);

        const uniqueSubs = [...new Set(subs)];

        if (!uniqueSubs.length) {
            console.log("Push skipped: no valid subscription ids");
            return;
        }

        try {
            await axios.post(
                "https://onesignal.com/api/v1/notifications",
                {
                    app_id: ONE_SIGNAL_APP_ID,
                    include_subscription_ids: uniqueSubs,
                    headings: { en: messageTitle },
                    contents: { en: messageBody },
                    android_channel_id: "2cafe22c-dd3f-43b4-bf4e-7ec9d1a5bf25",
                    data: {
                        screen: "ShopGroup",
                        groupId: groupId ?? 3,
                    },
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Basic ${ONE_SIGNAL_REST_API_KEY}`,
                    },
                }
            );
        } catch (error) {
            console.error("Push error:", error?.response?.data || error?.message);
        }
    };

    const fileToBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const result = String(reader.result || "");
                const base64 = result.includes(",") ? result.split(",")[1] : result;
                resolve(base64);
            };
            reader.onerror = reject;
        });

    const uploadOneToImgbb = async (base64) => {
        if (!base64) throw new Error("Şəkil base64 tapılmadı");
        let lastErr;

        for (const key of apiKeys) {
            try {
                const form = new FormData();
                form.append("key", key);
                form.append("image", base64);

                const res = await fetch("https://api.imgbb.com/1/upload", {
                    method: "POST",
                    body: form,
                });

                const json = await res.json();
                if (json && json.success && json.data && json.data.url) {
                    return json.data.url;
                }
                lastErr = new Error(json?.error?.message || "imgbb unknown error");
            } catch (e) {
                lastErr = e;
            }
        }

        throw lastErr || new Error("imgbb upload failed");
    };

    const uploadImagesToImgbb = async (imgs = []) => {
        const urls = [];

        for (const img of imgs) {
            const u = img?.uri;

            if (u && /^https?:\/\//i.test(u) && img.remote) {
                urls.push(u);
                await delay(100);
                continue;
            }

            let b64 = img?.base64;
            if (!b64 && img?.file) {
                b64 = await fileToBase64(img.file);
            }

            if (!b64) {
                console.warn("Base64 tapılmadı, şəkil atlandı:", u);
                continue;
            }

            const url = await uploadOneToImgbb(b64);
            if (url) urls.push(url);

            await delay(100);
        }

        return urls.filter(Boolean);
    };

    const pushImagesSequentially = async (elanId, urls) => {
        let acc = [];

        for (const u of urls) {
            if (!u) continue;

            acc = [...acc, u];
            const currentImages = [...acc];

            await requestWithRetry(() => updateElan(elanId, { images: currentImages }), {
                tries: 3,
                baseMs: 800,
            });

            await delay(800);
        }
    };

    const saveListing = async () => {
        if (!title.trim() || !price.trim()) {
            showModal({
                title: "Diqqət",
                message: "Başlıq və qiymət mütləqdir.",
                type: "warning",
            });
            return;
        }

        const parsedPrice = parsePrice(price);
        if (!Number.isFinite(parsedPrice) || parsedPrice <= 0) {
            showModal({
                title: "Diqqət",
                message: "Qiyməti düzgün formatda daxil edin. Məs: 35.5",
                type: "warning",
            });
            return;
        }

        try {
            setLoading(true);
            const uploadedUrls = await uploadImagesToImgbb(images);
            const effectiveUserId = await getEffectiveUserId(myUserId);
            if (!effectiveUserId) throw new Error("İstifadəçi ID tapılmadı.");

            const nowBaku = moment().tz("Asia/Baku").format("YYYY-MM-DD HH:mm:ss");

            if (editing) {
                const basePayload = {
                    userId: effectiveUserId,
                    title: title.trim(),
                    description: desc.trim(),
                    price: parsedPrice,
                    isDisable: false,
                    updatedAt: nowBaku,
                    ...(groupId ? { groupId } : {}),
                };

                await updateElan(editing.id, basePayload);
                await pushImagesSequentially(editing.id, uploadedUrls);
            } else {
                const createPayload = {
                    userId: effectiveUserId,
                    title: title.trim(),
                    description: desc.trim(),
                    price: parsedPrice,
                    isDisable: false,
                    createdAt: nowBaku,
                    updatedAt: nowBaku,
                    ...(groupId ? { groupId } : {}),
                    images: [],
                };

                const createRes = await createElan(createPayload);
                const newId = createRes?.data?.id;
                if (!newId) throw new Error("Yeni elan ID-si gəlmədi.");

                await pushImagesSequentially(newId, uploadedUrls);

                try {
                    const me = await getCustomUser(effectiveUserId);
                    const uname = me?.data?.name || me?.data?.username || "İstifadəçi";
                    const targets = await collectGroupPushTargets(groupId ?? 3, effectiveUserId);
                    if (targets.length) {
                        await sendPushNotification(
                            targets,
                            "🛒 Alqı-satqı",
                            `📦 ${uname} yeni elan: "${title.trim()}" — ${formatAZN(parsedPrice)}`
                        );
                    }
                } catch (e) {
                    console.log("Shop push skip:", e?.message);
                }
            }

            setEditorOpen(false);
            resetForm();
            await load();
        } catch (e) {
            console.log("saveElan error", e?.response?.status, e?.response?.data || e?.message);
            showModal({
                title: "Xəta",
                message: "Elan yadda saxlanılmadı.",
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const askDelete = async (id) => {
        const ok = window.confirm("Bu elanı silmək istəyirsiniz?");
        if (!ok) return;

        try {
            setLoading(true);
            await deleteElan(id);
            await load();
        } catch (e) {
            showModal({
                title: "Xəta",
                message: "Silinmədi.",
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const onPickImages = async (e) => {
        const files = Array.from(e.target.files || []).slice(0, 4);

        const selected = await Promise.all(
            files.map(async (file, idx) => ({
                id: `local-${Date.now()}-${idx}`,
                uri: URL.createObjectURL(file),
                file,
                remote: false,
                base64: await fileToBase64(file),
            }))
        );

        setImages((prev) => [...prev, ...selected].slice(0, 4));
        e.target.value = "";
    };

    const removeImageAt = (idx) => {
        setImages((prev) => prev.filter((_, i) => i !== idx));
    };

    const openViewer = (imgs = [], start = 0) => {
        const arr = Array.isArray(imgs) ? imgs.filter(Boolean) : [];
        if (!arr.length) return;
        setViewerImages(arr);
        setViewerIndex(start);
        setViewerOpen(true);
    };

    const contactWhatsApp = (phoneRaw, msg = "") => {
        const p = formatPhoneForWA(phoneRaw);
        if (!p) return;

        const url = `https://wa.me/${p}${msg ? `?text=${encodeURIComponent(msg)}` : ""}`;
        window.open(url, "_blank");
    };

    const renderCard = (item) => {
        const mine = Number(item.userId) === Number(myUserId);
        const imgs = item.images || [];
        const cover = imgs?.[0];
        const phone = phoneCache?.[Number(item.userId)];

        return (
            <div className="sq-shop-card">
                
                <div className="sq-shop-media-wrap">
                    {cover ? (
                        <button
                            type="button"
                            className="sq-shop-image-btn"
                            onClick={() => openViewer(imgs, 0)}
                        >
                            <img src={cover} alt={item.title} className="sq-shop-card-image" />
                        </button>
                    ) : (
                        <div className="sq-shop-card-image sq-shop-image-placeholder">
                            <IoImageOutline size={28} color="#9CA3AF" />
                            <span className="sq-shop-placeholder-text">Şəkil yoxdur</span>
                        </div>
                    )}

                    {!!item.price && (
                        <div className="sq-shop-price-pill">
                            <span className="sq-shop-price-pill-text">{formatAZN(item.price)}</span>
                        </div>
                    )}

                    {!!imgs?.length && (
                        <div className="sq-shop-count-badge">
                            <IoImagesOutline size={12} color="#fff" />
                            <span className="sq-shop-count-badge-text">{imgs.length}</span>
                        </div>
                    )}
                </div>

                <div className="sq-shop-card-body">
                    <div className="sq-shop-card-title">
                        {truncateWithEllipsis(item.title || "", 50)}
                    </div>

                    {!!item.description?.length && (
                        <>
                            <div className="sq-shop-card-desc">
                                {expandedDescIds.has(item.id)
                                    ? item.description
                                    : truncateWithEllipsis(item.description, 120)}
                            </div>

                            {!expandedDescIds.has(item.id) && item.description.length > 120 && (
                                <button
                                    type="button"
                                    className="sq-shop-read-more-btn"
                                    onClick={() => {
                                        setExpandedDescIds((prev) => {
                                            const next = new Set(prev);
                                            next.add(item.id);
                                            return next;
                                        });
                                    }}
                                >
                                    davamını oxu
                                </button>
                            )}
                        </>
                    )}

                    <div className="sq-shop-time-text">
                        {moment(item.createdAt || item.updatedAt).tz("Asia/Baku").fromNow()}
                    </div>

                    <div className="sq-shop-card-footer">
                        {!!phone && (
                            <button
                                type="button"
                                className="sq-shop-contact-btn"
                                onClick={() =>
                                    contactWhatsApp(
                                        phone,
                                        `Salam, Alqı-Satqı qrupundakı elanınızla bağlı yazıram. Elan hələ də aktivdir?: "${item.title}" (${formatAZN(item.price)})`
                                    )
                                }
                            >
                                <IoLogoWhatsapp size={16} color="#fff" />
                                <span className="sq-shop-contact-btn-text">Əlaqə</span>
                            </button>
                        )}

                        <div className="sq-shop-spacer" />
                    </div>

                    {mine && (
                        <div className="sq-shop-owner-row">
                            <button
                                type="button"
                                className="sq-shop-owner-btn"
                                onClick={() => openEditor(item)}
                            >
                                <IoCreateOutline size={16} color="#111827" />
                                <span className="sq-shop-owner-btn-text">Düzəlt</span>
                            </button>

                            <button
                                type="button"
                                className="sq-shop-owner-btn"
                                onClick={() => askDelete(item.id)}
                            >
                                <IoTrashOutline size={16} color="#DC2626" />
                                <span className="sq-shop-owner-btn-text sq-shop-owner-btn-danger">
                                    Sil
                                </span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="sq-shop-page">
            <div className="sq-shop-header">
                <div className="sq-shop-header-left">
                    <button
                        type="button"
                        className="sq-shop-back-btn"
                        onClick={() => navigate(-1)}
                    >
                        <IoArrowBack size={24} color="#fff" />
                    </button>

                    <div className="sq-shop-header-title-wrap">
                        <div className="sq-shop-header-title">{group?.name ?? "Alqı-satqı qrupu"}</div>
                    </div>
                </div>

                <div className="sq-shop-header-right">
                    <button type="button" className="sq-shop-mute-btn" onClick={toggleMute}>
                        {isMuted ? "🔊 Səsi aç" : "🔕 Səsi bağla"}
                    </button>

                    <button type="button" className="sq-shop-add-btn" onClick={openCreator}>
                        <IoAddCircle size={22} color="#075E54" />
                    </button>
                </div>
            </div>

            <div
                className="sq-shop-bg"
                style={{ backgroundImage: "url(/background.png)" }}
            >
                <div className="sq-shop-search-wrap">
                    <IoSearch size={18} color="#6B7280" />
                    <input
                        className="sq-shop-search-input"
                        placeholder="Axtarın…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    {search.length > 0 && (
                        <button
                            type="button"
                            className="sq-shop-search-clear"
                            onClick={() => setSearch("")}
                        >
                            <IoCloseCircle size={18} color="#9CA3AF" />
                        </button>
                    )}
                </div>

                <div className="sq-shop-list">
                    {listRows.length === 0 && !loading ? (
                        <div className="sq-shop-empty">Hələ elan yoxdur. İlk elanı yarat!</div>
                    ) : (
                        listRows.map((row) => {
                            if (row.type === "section") {
                                return (
                                    <div key={row.key} className="sq-shop-section-row">
                                        <div className="sq-shop-section-title">{row.title}</div>
                                    </div>
                                );
                            }

                            if (row.type === "row") {
                                return (
                                    <div key={row.key} className="sq-shop-grid-row">
                                        <div className="sq-shop-grid-col">
                                            {row.left ? renderCard(row.left) : null}
                                        </div>
                                        <div className="sq-shop-grid-col">
                                            {row.right ? renderCard(row.right) : null}
                                        </div>
                                    </div>
                                );
                            }

                            return null;
                        })
                    )}
                </div>
            </div>

            {editorOpen && (
                <div className="sq-modal-overlay">
                    <div className="sq-shop-modal-sheet">
                        <div className="sq-shop-modal-header">
                            <div className="sq-shop-modal-title">
                                {editing ? "Elanı Düzəlt" : "Yeni Elan"}
                            </div>
                            <button
                                type="button"
                                className="sq-shop-modal-close"
                                onClick={() => setEditorOpen(false)}
                            >
                                <IoClose size={24} color="#111827" />
                            </button>
                        </div>

                        <div className="sq-shop-modal-body">
                            <div className="sq-shop-form-row">
                                <label className="sq-shop-label">Başlıq *</label>
                                <input
                                    className="sq-shop-input"
                                    placeholder="Məs: Moto yaxşı vəziyyətdə"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                />
                            </div>

                            <div className="sq-shop-form-row">
                                <label className="sq-shop-label">Açıqlama</label>
                                <textarea
                                    className="sq-shop-input sq-shop-textarea"
                                    placeholder="Təsvir…"
                                    value={desc}
                                    onChange={(e) => setDesc(e.target.value)}
                                />
                            </div>

                            <div className="sq-shop-form-row">
                                <label className="sq-shop-label">Qiymət (AZN) *</label>
                                <input
                                    className="sq-shop-input"
                                    placeholder="35"
                                    value={price}
                                    onChange={(e) => setPrice(e.target.value)}
                                />
                            </div>

                            <div className="sq-shop-form-row">
                                <label className="sq-shop-label">Şəkillər (maks 4)</label>

                                <div className="sq-shop-picks-row">
                                    <button
                                        type="button"
                                        className="sq-shop-add-image"
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        <IoAddCircle size={28} color="#6B7280" />
                                        <span className="sq-shop-add-image-text">Şəkil seç</span>
                                    </button>

                                    {images.map((img, idx) => (
                                        <div key={img.id || idx} className="sq-shop-pick-thumb-wrap">
                                            <img src={img.uri} alt="" className="sq-shop-pick-thumb" />
                                            <button
                                                type="button"
                                                className="sq-shop-remove-chip"
                                                onClick={() => removeImageAt(idx)}
                                            >
                                                <IoClose size={14} color="#fff" />
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    hidden
                                    onChange={onPickImages}
                                />
                            </div>

                            <button
                                type="button"
                                className="sq-shop-save-btn"
                                onClick={saveListing}
                                disabled={loading}
                            >
                                {loading ? "Gözləyin..." : editing ? "Yadda saxla" : "Elanı Yarat"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {viewerOpen && (
                <div className="sq-preview-backdrop" onClick={() => setViewerOpen(false)}>
                    <div
                        className="sq-preview-content"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="sq-preview-close"
                            onClick={() => setViewerOpen(false)}
                        >
                            ✕
                        </button>

                        <div className="sq-shop-viewer-counter">
                            {viewerIndex + 1}/{viewerImages.length}
                        </div>

                        <img
                            src={viewerImages[viewerIndex]}
                            alt="Preview"
                            className="sq-preview-image"
                        />

                        {viewerImages.length > 1 && (
                            <div className="sq-shop-viewer-nav">
                                <button
                                    type="button"
                                    className="sq-shop-viewer-nav-btn"
                                    onClick={() =>
                                        setViewerIndex((prev) =>
                                            prev === 0 ? viewerImages.length - 1 : prev - 1
                                        )
                                    }
                                >
                                    ‹
                                </button>

                                <button
                                    type="button"
                                    className="sq-shop-viewer-nav-btn"
                                    onClick={() =>
                                        setViewerIndex((prev) =>
                                            prev === viewerImages.length - 1 ? 0 : prev + 1
                                        )
                                    }
                                >
                                    ›
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {modal.open && (
                <div className="sq-modal-overlay">
                    <div className="sq-alert-modal">
                        <h3 className={`sq-alert-title sq-alert-${modal.type}`}>
                            {modal.title}
                        </h3>
                        <p className="sq-alert-message">{modal.message}</p>
                        <button
                            type="button"
                            className="sq-alert-button"
                            onClick={() =>
                                setModal({
                                    open: false,
                                    title: "",
                                    message: "",
                                    type: "info",
                                })
                            }
                        >
                            Bağla
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ShopGroupScreen;